import { RefinementCtx, ZodError, ZodIssue, ZodIssueCode } from 'my-zod'
import { SalesQuoteSeasonTypeLabels } from './sales-quote-season-type'
import { salesQuoteSeasonsModel } from './sales-quote-seasons-model'
import { modelFamilySystemView } from '../../fleet-operations/model-family'
import { overlap, getColumnIndicesWithValue, getRowIndicesWithValue, findClosestInList } from './sales-quote-season-helpers'
import {
  Orv_Peak_Pricing_Matrix,
  Expired_Orv_Peak_Pricing_Matrix,
  Orv_Pricing_Matrix,
  Expired_Orv_Pricing_Matrix,
  Atv_Pricing_Matrix,
  Expired_Atv_Pricing_Matrix,
  Slg_Pricing_Matrix,
  Expired_Slg_Pricing_Matrix,
  Marine_Pricing_Matrix
} from './sales-quote-season-matrices'
import {
  getSalesQuoteSeasonVehicleCount,
  getSeasonDefaultProgramFee,
  getSeasonDefaultRideFee,
  getSalesQuoteSeasonBillingPeriods
} from './sales-quote-season-calculations'
import { SalesQuoteType, SalesQuoteTypeEnum } from './sales-quote-headers-model'

type AllowedSeasonFilter = 'all' | 'latest'

export function validateVehicleCountBySeason(
  season: salesQuoteSeasonsModel,
  ctx: RefinementCtx,
  filter: AllowedSeasonFilter = 'all'
) {
  const vehicleCountOptions = getAllowedSeasonVehicleCounts(season, filter)
  const vehicleCount = getSalesQuoteSeasonVehicleCount(season)

  // if not customized, vehicle count must allowed
  if (!season.isCustomized && !vehicleCountOptions.includes(vehicleCount)) {
    const suggestedVehicleCount = findClosestInList(vehicleCountOptions, vehicleCount)
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: `${vehicleCount} is not an allowed vehicle count for ${
        SalesQuoteSeasonTypeLabels[season.type]
      }. Consider quoting ${suggestedVehicleCount} vehicles or customizing.`,
      path: ['lineItems']
    })
  }
}

function getAllowedSeasonVehicleCounts(season: Pick<salesQuoteSeasonsModel, 'type'>, filter: AllowedSeasonFilter): number[] {
  switch (season.type) {
    case 'ATV':
      return filter === 'all'
        ? [...getRowIndicesWithValue(Atv_Pricing_Matrix), ...getRowIndicesWithValue(Expired_Atv_Pricing_Matrix)]
        : getRowIndicesWithValue(Atv_Pricing_Matrix)
    case 'CUSTOM':
      return Array.from(Array(300), (_, i) => i + 1)
    case 'IMX':
      return [8]
    case 'MARINE':
      return getRowIndicesWithValue(Marine_Pricing_Matrix)
    case 'ORV_PEAK':
      return filter === 'all'
        ? [...getRowIndicesWithValue(Orv_Peak_Pricing_Matrix), ...getRowIndicesWithValue(Expired_Orv_Peak_Pricing_Matrix)]
        : getRowIndicesWithValue(Orv_Peak_Pricing_Matrix)
    case 'ORV':
      return filter === 'all'
        ? [...getRowIndicesWithValue(Orv_Pricing_Matrix), ...getRowIndicesWithValue(Expired_Orv_Pricing_Matrix)]
        : getRowIndicesWithValue(Orv_Pricing_Matrix)
    case 'SLG':
      return filter === 'all'
        ? [...getRowIndicesWithValue(Slg_Pricing_Matrix), ...getRowIndicesWithValue(Expired_Slg_Pricing_Matrix)]
        : getRowIndicesWithValue(Slg_Pricing_Matrix)
    case 'SNO':
      return Array.from(Array(300), (_, i) => i + 1)
    case 'SNO_PERFORMANCE':
      return Array.from(Array(300), (_, i) => i + 1)
    default:
      throw new Error(`Unrecognized holding term type: ${season.type}`)
  }
}

export function validateAllowedSeasonMonthCounts(
  season: salesQuoteSeasonsModel,
  ctx: RefinementCtx,
  filter: AllowedSeasonFilter = 'all'
) {
  const activeMonthOptions = getAllowedSeasonMonthCounts(season, filter)
  // if not customized, active month count must be available
  const numberOfActiveMonths = getSalesQuoteSeasonBillingPeriods(season).length
  if (!season.isCustomized && !activeMonthOptions.includes(numberOfActiveMonths)) {
    const suggestedMonthCount = findClosestInList(activeMonthOptions, numberOfActiveMonths)
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: `${numberOfActiveMonths} is not an allowed number of months for ${
        SalesQuoteSeasonTypeLabels[season.type]
      }. Consider quoting ${suggestedMonthCount} months or customizing.`,
      path: ['numberOfMonths']
    })
  }
}

function getAllowedSeasonMonthCounts(season: Pick<salesQuoteSeasonsModel, 'type'>, filter: AllowedSeasonFilter): number[] {
  switch (season.type) {
    case 'ATV':
      return getColumnIndicesWithValue(Atv_Pricing_Matrix)
    case 'CUSTOM':
      return Array.from(Array(12), (_, i) => i + 1)
    case 'IMX':
      return [6, 12]
    case 'MARINE':
      return getColumnIndicesWithValue(Marine_Pricing_Matrix)
    case 'ORV_PEAK':
      return getColumnIndicesWithValue(Orv_Peak_Pricing_Matrix)
    case 'ORV':
      return getColumnIndicesWithValue(Orv_Pricing_Matrix)
    case 'SLG':
      return getColumnIndicesWithValue(Slg_Pricing_Matrix)
    case 'SNO':
      return filter === 'all'
        ? Array.from(Array(12), (_, i) => i + 1) // old value (full year)
        : [3, 4] // only snow months
    case 'SNO_PERFORMANCE':
      return Array.from(Array(12), (_, i) => i + 1)
    default:
      throw new Error(`Unrecognized holding term type: ${season.type}`)
  }
}

export function validateIMXMonthCount(season: Pick<salesQuoteSeasonsModel, 'type' | 'numberOfMonths'>, ctx: RefinementCtx) {
  if (season.type === 'IMX' && season.numberOfMonths !== 6 && season.numberOfMonths !== 12) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: `For ${season.type} only 6 and 12 month payment plans are available.`,
      path: ['numberOfMonths']
    })
  }
}

export function validateSNOMonthCount(season: Pick<salesQuoteSeasonsModel, 'type' | 'numberOfMonths'>, ctx: RefinementCtx) {
  if (season.type === 'SNO' && season.numberOfMonths !== 3 && season.numberOfMonths !== 4) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: `For ${season.type} only 3 and 4 month payment plans are available.`,
      path: ['numberOfMonths']
    })
  }
}

export function getSalesQuoteSeasonWarnings(
  season: salesQuoteSeasonsModel,
  otherSeasons: salesQuoteSeasonsModel[],
  modelFamilies: modelFamilySystemView[],
  extraProgramExpense: number,
  headerType: SalesQuoteType
): ZodError {
  const warnings: ZodIssue[] = []

  function warn(message: string, path: (string | number)[]) {
    warnings.push({
      code: ZodIssueCode.custom,
      message,
      path
    })
  }

  const suggestedProgramFee = getSeasonDefaultProgramFee(season, modelFamilies, extraProgramExpense)

  if (
    headerType !== SalesQuoteTypeEnum.Values.FleetSale &&
    season.programFeePerMonth &&
    suggestedProgramFee &&
    suggestedProgramFee !== season.programFeePerMonth
  ) {
    warn(`Suggested Use Fee: ${suggestedProgramFee}`, ['programFeePerMonth'])
  }

  const suggestedRideFee = getSeasonDefaultRideFee(season)

  if (
    headerType !== SalesQuoteTypeEnum.Values.FleetSale &&
    suggestedRideFee &&
    (season.rideFee || season.rideFee === 0) &&
    suggestedRideFee !== season.rideFee
  ) {
    warn(`Suggested Ride Fee: ${suggestedRideFee}`, ['rideFee'])
  }

  if (season.type === 'SLG' && season.numberOfMonths > 24) {
    warn(`${season.type} Holding term cannot be longer than 24 months`, ['numberOfMonths'])
  }

  if (season.isCustomized) {
    warn(`This holding term is customized`, [])
  }

  // TODO: candidate to be retired
  // earliestVehicleDeliveryDate cannot come after the targetVehicleDeliveryDate
  // if (season.earliestVehicleDeliveryDate > season.targetVehicleDeliveryDate) {
  //   warn(`earliestVehicleDeliveryDate cannot come after the targetVehicleDeliveryDate`, ['earliestVehicleDeliveryDate'])
  // }

  if (season.type === 'SLG') {
    season.activePeriods.forEach((period, i) => {
      // period should be a minimum of 4 months
      if (period.numberOfMonths < 4) {
        warn('Period should be a minimum of 4 months', ['activePeriods', i])
      }
    })
  }
  if (!getSalesQuoteSeasonVehicleCount(season)) {
    warn(`A holding term must have at least one vehicle.`, ['lineItems'])
  }

  if (
    headerType !== SalesQuoteTypeEnum.Values.FleetSale &&
    season.programPricingPlan === 'PerMonthPricing' &&
    !season.programFeePerMonth
  ) {
    warn('Use Fee Per Month is Required with Per Month Pricing', ['programFeePerMonth'])
  }

  if (season.programPricingPlan === 'PerRidePricing' && season.programFeePerMonth) {
    warn(`Cannot specify a programFeePerMonth with PerRidePricing.`, ['programFeePerMonth'])
  }

  if (headerType !== SalesQuoteTypeEnum.Values.FleetSale && season.programPricingPlan === 'PerRidePricing') {
    const isProgramFeePerRideEmpty = !season.programFeePerRide
    const isMinimumProgramFeePerMonthEmpty = !season.minimumProgramFeePerMonth

    if (isProgramFeePerRideEmpty && isMinimumProgramFeePerMonthEmpty) {
      warn('Either Use Fee Per Ride or Minimum Monthly Use Fee must be > $0', ['programFeePerRide'])
      warn('Either Use Fee Per Ride or Minimum Monthly Use Fee must be > $0', ['minimumProgramFeePerMonth'])
    }
  }

  // active periods should be non overlapping
  season.activePeriods.every((periodA, index) => {
    const rest = season.activePeriods.filter((a) => a !== periodA)
    const anyOverlapping = rest.some((periodB) => overlap(periodA, periodB))
    if (anyOverlapping) {
      warn(`Active Periods must be non-overlapping.`, ['activePeriods', index])
    }
  })

  const isSeasonOverlap = otherSeasons
    .filter((os) => os.type === season.type)
    .some((os) =>
      overlap(
        { startDate: os.contractedStartDate, numberOfMonths: os.numberOfMonths },
        { startDate: season.contractedStartDate, numberOfMonths: season.numberOfMonths }
      )
    )

  if (isSeasonOverlap) {
    warn(`This holding term overlaps with another holding term of the same type`, ['contractedStartDate'])
    warn(`This holding term overlaps with another holding term of the same type`, ['numberOfMonths'])
  }

  return new ZodError(warnings)
}
